import { useState } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@corpuscomplete/web-ui/components/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@corpuscomplete/web-ui/components/empty";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { useAuth, useUser } from "@corpuscomplete/web/auth/hooks";
import { createId } from "@corpuscomplete/core/utils";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { PlusIcon, UsersIcon } from "lucide-react";

export function GroupsPage() {
  const z = useZero();
  const { userId } = useAuth();
  const user = useUser();
  const [groups, groupsDetails] = useQuery(queries.space.byMembership());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");

  if (groupsDetails.type === "unknown") {
    return null;
  }

  const defaultName = `${user.name}'s Group`;

  const handleCreateGroup = () => {
    const name = groupName.trim() || defaultName;
    const id = createId();
    z.mutate(
      mutators.space.create({
        id,
        name,
        members: [{ userId }],
      }),
    );
    setDialogOpen(false);
    setGroupName("");
  };

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text className="mr-auto">Groups</Text>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger render={<Button variant="outline" size="sm" />}>
              <PlusIcon className="mr-1.5 size-4" />
              Create Group
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Group</DialogTitle>
                <DialogDescription>
                  Create a new group to share recipes, meal plans, and more with others.
                </DialogDescription>
              </DialogHeader>
              <Input
                placeholder={defaultName}
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleCreateGroup();
                  }
                }}
                autoFocus
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateGroup}>Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PageHeader>
        <PageContent>
          {groups.length === 0 ? (
            <Empty className="flex-1">
              <EmptyHeader>
                <EmptyMedia>
                  <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                    <UsersIcon className="size-8 text-muted-foreground" />
                  </div>
                </EmptyMedia>
                <EmptyTitle>No groups yet</EmptyTitle>
                <EmptyDescription>
                  Create a group to share recipes, meal plans, and shopping lists with family or
                  friends.
                </EmptyDescription>
              </EmptyHeader>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger render={<Button />}>
                  <PlusIcon className="mr-1.5 size-4" />
                  Create Your First Group
                </DialogTrigger>
              </Dialog>
            </Empty>
          ) : (
            <div className="flex flex-col">
              {groups.map((group) => (
                <Link
                  to="/groups/$id"
                  params={{ id: group.id }}
                  key={group.id}
                  className="flex flex-row items-center gap-3 p-3 hover:cursor-default hover:bg-muted"
                >
                  <div className="flex size-10 items-center justify-center rounded-full bg-muted">
                    <UsersIcon className="size-5 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col">
                    <Text>{group.name}</Text>
                    <Text variant="xs" className="text-muted-foreground">
                      {group.group?.members.length ?? 0}{" "}
                      {group.group?.members.length === 1 ? "member" : "members"}
                    </Text>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </PageContent>
      </PageBody>
    </Page>
  );
}
